import React from 'react';
import not from 'ramda/src/not';
import isNil from 'ramda/src/isNil';
import Head from 'next/head';
import { AxiosError, AxiosResponse } from 'axios';
import { ConditionArticlePageContainer, ConditionArticlePageContainerValue } from '../../containers/ConditionArticlePageContainer';
import { BranchScript } from '../../components/BranchScript';
import { getArticleBySlugResponseTransformer } from '../../transformers/get-article-by-slug-response-transformer';
import { healthArticleToHealthArticlePageTransformer } from '../../transformers/health-article-to-health-article-page-transformer';
import { BASE_URL, BRANCH_KEY, HealthArticleType } from '../../constants/app';
import { DEFAULT_CACHE_POLICY } from '../../constants/cache';
import { WagApiEndpoint } from '../../constants/endpoints';
import { Routes } from '../../constants/routes';
import { axiosContent } from '../../utils/axios';
import { breadcrumbsBuilder } from '../../utils/breadcrumbsBuilder';
import { isNotFoundException } from '../../utils/api/isNotFoundException';
import { isRedirectException } from '../../utils/api/isRedirectException';
import { NotFoundException } from '../../utils/exceptions/NotFoundException';
import { tryCatch } from '../../utils/tryCatch';
import { RedirectException } from '../../utils/exceptions/RedirectException';
import { httpException } from '../../utils/exceptions/http.exception';
import { extractHref, getPrevNextUrl } from '../../utils';
import { Headers, getRedirectLocation } from '../../utils/api/getRedirectLocation';
import { HealthArticleResponse } from '../../types/api/healthArticle';
import { applyPathParams } from '../../wag-react/common/api/utils/applyPathParams';
type PageMeta = Record<'metaDesc' | 'metaTitle', string>;
type Props = {
  value: ConditionArticlePageContainerValue;
  meta: PageMeta;
  slug: string;
  hasNextPage: boolean;
  nextPageUrl: null | string;
  prevPageUrl: null | string;
  href: string;
};
const ConditionArticlePage = ({
  value,
  meta: {
    metaDesc,
    metaTitle
  },
  slug,
  hasNextPage,
  nextPageUrl,
  prevPageUrl,
  href
}: Props) => <>
    <Head data-sentry-element="Head" data-sentry-source-file="[slug].tsx">
      <title>{metaTitle}</title>
      <link rel="canonical" href={href} />
      {prevPageUrl && <link rel="prev" href={prevPageUrl} />}
      {nextPageUrl && <link rel="next" href={nextPageUrl} />}
      <meta name="robots" content="index, follow" data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
      <meta name="description" content={metaDesc} data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />

      {/* Twitter */}
      <meta name="twitter:title" content={metaTitle} data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
      <meta name="twitter:description" content={metaDesc} data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
      <meta name="twitter:image" content={value.articlePhotoSrc} data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />

      {/* OpenGraph Facebook Markup */}
      <meta property="og:title" content={metaTitle} data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
      <meta property="og:description" content={metaDesc} data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
      <meta property="og:image" content={value.articlePhotoSrc} data-sentry-element="meta" data-sentry-source-file="[slug].tsx" />
    </Head>
    <BranchScript branchKey={BRANCH_KEY} data-sentry-element="BranchScript" data-sentry-source-file="[slug].tsx" />
    <ConditionArticlePageContainer value={value} metaTitle={metaTitle} nextPageUrl={nextPageUrl} slug={slug} hasNextPage={hasNextPage} data-sentry-element="ConditionArticlePageContainer" data-sentry-source-file="[slug].tsx" />
  </>;
export const getServerSideProps = async ({
  req,
  res,
  query
}) => {
  const {
    slug,
    page = 1
  } = query;
  const url = applyPathParams(WagApiEndpoint.GetConditionArticleBySlug)({
    slug
  });

  // NOTE: If editing WagContent Requests or cache-policy, search code for key WCREQ
  const [response, error] = await tryCatch<AxiosResponse<HealthArticleResponse>, AxiosError>(() => axiosContent.get(url, {
    params: {
      page
    },
    maxRedirects: 0
  }));
  if (error) {
    const statusCode = error.response.status;
    if (isNotFoundException(statusCode)) {
      throw NotFoundException(`Not found article: ${slug}`);
    }
    if (isRedirectException(statusCode)) {
      const redirectLocation = getRedirectLocation(error.response.headers as Headers);
      throw RedirectException(statusCode, redirectLocation);
    }
    throw httpException({
      statusCode
    });
  }
  res.setHeader('Cache-Control', DEFAULT_CACHE_POLICY);
  const transformedData = getArticleBySlugResponseTransformer(response.data);
  const componentData = healthArticleToHealthArticlePageTransformer({
    ...transformedData,
    type: HealthArticleType.Condition
  });
  const {
    nextPage,
    prevPage
  } = transformedData;
  const hasNextPage = not(isNil(nextPage));
  const {
    metaTitle,
    metaDesc,
    title,
    ...restComponentData
  } = componentData;
  const filteredComponentData = Object.keys(restComponentData).reduce((acc, key) => {
    // Undefined can't be serialized
    if (restComponentData[key] === undefined) {
      restComponentData[key] = null;
    } else {
      acc[key] = restComponentData[key];
    }
    return acc;
  }, {});
  const href = extractHref(req, BASE_URL, prevPage, page);
  const {
    prevPageUrl,
    nextPageUrl
  } = getPrevNextUrl(prevPage, nextPage, href);
  const breadcrumbItems = breadcrumbsBuilder().add({
    title: 'Dog Conditions',
    url: Routes.ConditionArticleDirectory
  }).add({
    title
  }).build();
  return {
    props: {
      value: {
        title,
        breadcrumbItems,
        ...filteredComponentData
      },
      meta: {
        metaTitle,
        metaDesc
      },
      href,
      slug,
      hasNextPage,
      nextPageUrl,
      prevPageUrl
    }
  };
};
export default ConditionArticlePage;